import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Tab = 'jobs' | 'plans' | 'machines';
type JobType = 'preventive' | 'breakdown' | 'corrective';
type JobStatus = 'open' | 'in_progress' | 'completed' | 'cancelled';

interface Job {
  id: string;
  jobNumber: string;
  machineId: string;
  machineCode: string;
  machineName: string;
  type: JobType;
  status: JobStatus;
  priority: string;
  problem: string;
  checklist: string | null;
  reportedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  technician: string | null;
  workDone: string | null;
  cause: string | null;
  labourHours: number;
  labourCost: number;
  partsCost: number;
  totalCost: number;
  stopsMachine: boolean;
  downtimeEntryId: string | null;
  cancelReason: string | null;
  elapsedMinutes: number;
  reportedBy: string | null;
  parts?: { id: string; productName: string; sku: string | null; unit: string | null; quantity: number; unitCost: number; value: number }[];
}
interface Plan {
  id: string;
  machineId: string;
  machineCode: string;
  machineName: string;
  name: string;
  frequencyDays: number;
  checklist: string | null;
  estimatedMinutes: number | null;
  lastDoneOn: string | null;
  nextDueOn: string;
  isActive: boolean;
  daysLeft: number;
  due: 'overdue' | 'soon' | 'later';
  openJobNumber: string | null;
}
interface MachineRow {
  id: string;
  code: string;
  name: string;
  workCenterId: string | null;
  workCenterName: string | null;
  location: string | null;
  make: string | null;
  model: string | null;
  serialNumber: string | null;
  commissionedOn: string | null;
  status: 'running' | 'down' | 'retired';
  notes: string | null;
  openJobs: number;
  nextDueOn: string | null;
  overduePlans: number;
}
interface Machine extends Omit<MachineRow, 'plans'> {
  plans: Plan[];
  jobs: Job[];
  reliability: { days: number; breakdowns: number; meanRepairHours: number | null; meanDaysBetweenBreakdowns: number | null; hoursDown: number; cost: number };
}
interface Page<T> {
  data: T[];
  total: number;
  totalPages: number;
}
interface Summary {
  machinesRunning: number;
  machinesDown: number;
  openJobs: number;
  breakdownsThisMonth: number;
  costThisMonth: number;
  plansOverdue: number;
  plansDueThisWeek: number;
}
interface Choice {
  id: string;
  name: string;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
  currentStock: number;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const TYPE: Record<JobType, { label: string; className: string }> = {
  breakdown: { label: 'Breakdown', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  corrective: { label: 'Corrective', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  preventive: { label: 'Preventive', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
};
const STATUS: Record<JobStatus, { label: string; className: string }> = {
  open: { label: 'Open', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  in_progress: { label: 'In progress', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  completed: { label: 'Completed', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};
const MACHINE: Record<MachineRow['status'], { label: string; className: string }> = {
  running: { label: 'Running', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  down: { label: 'Down', className: 'bg-red-50 text-red-700 ring-red-600/20' },
  retired: { label: 'Retired', className: 'bg-gray-50 text-gray-500 ring-gray-500/20' },
};
const pad = (value: number) => String(value).padStart(2, '0');
const localInput = (value: Date | string) => {
  const date = new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatMoment = (value?: string | null) =>
  value ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
const duration = (minutes: number) => {
  const whole = Math.max(0, Math.round(minutes));
  if (whole < 60) return `${whole} min`;
  if (whole < 2880) return `${Math.floor(whole / 60)} h${whole % 60 ? ` ${whole % 60} min` : ''}`;
  return `${formatNumber(Math.round(whole / 1440))} days`;
};
const dueText = (plan: Plan) => (plan.daysLeft < 0 ? `${formatNumber(-plan.daysLeft)} day(s) overdue` : plan.daysLeft === 0 ? 'Due today' : `in ${formatNumber(plan.daysLeft)} day(s)`);
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));
const listOf = <T,>(result: unknown): T[] => (Array.isArray(result) ? result : ((result as { data?: T[] })?.data ?? [])) as T[];

const maintenanceApi = {
  summary: async (): Promise<Summary> => (await api.get('/maintenance/summary')).data,
  jobs: async (params: object): Promise<Page<Job>> => (await api.get('/maintenance/jobs', { params: clean(params) })).data,
  job: async (id: string): Promise<Job> => (await api.get(`/maintenance/jobs/${id}`)).data,
  createJob: async (data: object): Promise<Job> => (await api.post('/maintenance/jobs', data)).data,
  jobAction: async (id: string, action: string, data: object = {}): Promise<Job> => (await api.post(`/maintenance/jobs/${id}/${action}`, data)).data,
  plans: async (params: object): Promise<Page<Plan>> => (await api.get('/maintenance/plans', { params: clean(params) })).data,
  savePlan: async (id: string | null, data: object): Promise<Plan> => (id ? await api.put(`/maintenance/plans/${id}`, data) : await api.post('/maintenance/plans', data)).data,
  removePlan: async (id: string): Promise<void> => {
    await api.delete(`/maintenance/plans/${id}`);
  },
  raise: async (planId: string): Promise<Job> => (await api.post(`/maintenance/plans/${planId}/raise`)).data,
  machines: async (params: object): Promise<Page<MachineRow>> => (await api.get('/maintenance/machines', { params: clean(params) })).data,
  machine: async (id: string): Promise<Machine> => (await api.get(`/maintenance/machines/${id}`)).data,
  saveMachine: async (id: string | null, data: object): Promise<Machine> => (id ? await api.put(`/maintenance/machines/${id}`, data) : await api.post('/maintenance/machines', data)).data,
  machineAction: async (id: string, action: 'retire' | 'reinstate'): Promise<Machine> => (await api.post(`/maintenance/machines/${id}/${action}`)).data,
  removeMachine: async (id: string): Promise<void> => {
    await api.delete(`/maintenance/machines/${id}`);
  },
  workCenters: async (): Promise<Choice[]> => listOf<Choice>((await api.get('/master-data/work-centers', { params: { limit: 100 } })).data),
  downtimeReasons: async (): Promise<(Choice & { isPlanned: boolean })[]> => (await api.get('/manufacturing/downtime-reasons', { params: { activeOnly: true } })).data,
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
};
// Machines that can still have work raised on them, for the pickers
const useMachineChoices = () => useQuery({ queryKey: ['maintenance', 'machine-choices'], queryFn: async () => (await maintenanceApi.machines({ limit: 100 })).data.filter((machine) => machine.status !== 'retired') });

// ---------------------------------------------------------------- report a breakdown or raise corrective work

const JobForm: React.FC<{ isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ isPending, onCancel, onSubmit }) => {
  const { data: machines = [] } = useMachineChoices();
  const { data: reasons = [] } = useQuery({ queryKey: ['maintenance', 'downtime-reasons'], queryFn: maintenanceApi.downtimeReasons });
  const [form, setForm] = useState({ machineId: '', type: 'breakdown' as 'breakdown' | 'corrective', problem: '', priority: '', reportedAt: localInput(new Date()), stopsMachine: true, downtimeReasonId: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const machine = machines.find((candidate) => candidate.id === form.machineId);
  const stops = form.type === 'breakdown' && form.stopsMachine;
  const canLog = stops && !!machine?.workCenterId;
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors({});
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!form.machineId) next.machineId = 'Choose the machine';
    if (form.problem.trim().length < 3) next.problem = 'Say what is wrong';
    if (!form.reportedAt) next.reportedAt = 'Enter when it was noticed';
    else if (new Date(form.reportedAt).getTime() > Date.now() + 60000) next.reportedAt = 'Not in the future';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      machineId: form.machineId,
      type: form.type,
      problem: form.problem.trim(),
      priority: form.priority || undefined,
      reportedAt: new Date(form.reportedAt).toISOString(),
      stopsMachine: stops,
      downtimeReasonId: canLog && form.downtimeReasonId ? form.downtimeReasonId : null,
    });
  };
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Machine" htmlFor="mj-machine" required error={errors.machineId} hint={machines.length === 0 ? 'Add machines on the Machines tab first' : undefined}>
          <select id="mj-machine" value={form.machineId} onChange={set('machineId')} className={cn(inputClass, errors.machineId && inputErrorClass)}>
            <option value="">Choose…</option>
            {machines.map((candidate) => (<option key={candidate.id} value={candidate.id}>{candidate.code} · {candidate.name}</option>))}
          </select>
        </Field>
        <Field label="Kind" htmlFor="mj-type" required>
          <select id="mj-type" value={form.type} onChange={set('type')} className={inputClass}>
            <option value="breakdown">Breakdown: it has failed</option>
            <option value="corrective">Corrective: needs putting right</option>
          </select>
        </Field>
        <Field label="What is wrong" htmlFor="mj-problem" required error={errors.problem} className="sm:col-span-2">
          <textarea id="mj-problem" rows={2} maxLength={2000} value={form.problem} onChange={set('problem')} className={cn(inputClass, errors.problem && inputErrorClass)} />
        </Field>
        <Field label="Noticed at" htmlFor="mj-reported" required error={errors.reportedAt}>
          <input id="mj-reported" type="datetime-local" value={form.reportedAt} onChange={set('reportedAt')} className={cn(inputClass, errors.reportedAt && inputErrorClass)} />
        </Field>
        <Field label="Priority" htmlFor="mj-priority">
          <select id="mj-priority" value={form.priority} onChange={set('priority')} className={inputClass}>
            <option value="">{form.type === 'breakdown' ? 'High (usual for a breakdown)' : 'Medium'}</option>
            {['urgent', 'high', 'medium', 'low'].map((priority) => (<option key={priority} value={priority}>{priority.charAt(0).toUpperCase() + priority.slice(1)}</option>))}
          </select>
        </Field>
      </div>
      {form.type === 'breakdown' && (
        <div className="space-y-3 rounded-lg bg-gray-50 p-3 ring-1 ring-gray-200">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input id="mj-stops" type="checkbox" checked={form.stopsMachine} onChange={(event) => setForm((prev) => ({ ...prev, stopsMachine: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            The machine cannot run until this is repaired
          </label>
          {stops && machine && !machine.workCenterId && <p className="text-xs text-gray-500">{machine.code} belongs to no work center, so nothing goes into the downtime log.</p>}
          {canLog && (
            <Field label={`Stop ${machine?.workCenterName ?? 'its work center'} in the downtime log under`} htmlFor="mj-reason" hint="The stop ends when the job is completed. Left empty, no stop is logged.">
              <select id="mj-reason" value={form.downtimeReasonId} onChange={set('downtimeReasonId')} className={inputClass}>
                <option value="">Do not log a stop</option>
                {reasons.filter((reason) => !reason.isPlanned).map((reason) => (<option key={reason.id} value={reason.id}>{reason.name}</option>))}
              </select>
            </Field>
          )}
        </div>
      )}
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : form.type === 'breakdown' ? 'Report Breakdown' : 'Raise Job'}</button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- one job: start it, complete it, cancel it

const JobPanel: React.FC<{ jobId: string; canEdit: boolean; onClose: () => void; onChanged: () => void }> = ({ jobId, canEdit, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: job, isLoading, isError, error } = useQuery({ queryKey: ['maintenance', 'job', jobId], queryFn: () => maintenanceApi.job(jobId) });
  const [mode, setMode] = useState<'view' | 'complete' | 'cancel'>('view');
  const [technician, setTechnician] = useState('');
  const [done, setDone] = useState({ workDone: '', cause: '', labourHours: '', labourCost: '' });
  const [parts, setParts] = useState<{ productId: string; name: string; sku: string; unit: string; stock: number; quantity: string }[]>([]);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data: found = [], isFetching } = useQuery({ queryKey: ['maintenance', 'products', search], queryFn: () => maintenanceApi.products(search), enabled: search.length >= 2 && mode === 'complete' });

  const act = useMutation({
    mutationFn: ({ action, data }: { action: string; data?: object }) => maintenanceApi.jobAction(jobId, action, data),
    onSuccess: (saved, { action }) => {
      toast.success(action === 'start' ? `${saved.jobNumber} started` : action === 'complete' ? `${saved.jobNumber} completed${saved.totalCost > 0 ? ` at ${formatMoney(saved.totalCost)}` : ''}` : `${saved.jobNumber} cancelled`);
      queryClient.setQueryData(['maintenance', 'job', jobId], saved);
      ['products', 'warehouses', 'oee'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      setMode('view');
      onChanged();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 }),
  });

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !job) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The job could not be loaded.')}</p>;
  const pending = job.status === 'open' || job.status === 'in_progress';

  const complete = () => {
    const next: Record<string, string> = {};
    if (done.workDone.trim().length < 3) next.workDone = 'Say what was done';
    const hoursWorked = done.labourHours.trim() === '' ? 0 : Number(done.labourHours);
    const cost = done.labourCost.trim() === '' ? 0 : Number(done.labourCost);
    if (!Number.isFinite(hoursWorked) || hoursWorked < 0) next.labourHours = 'Enter a number';
    if (!Number.isFinite(cost) || cost < 0) next.labourCost = 'Enter an amount';
    parts.forEach((part, index) => {
      const quantity = Number(part.quantity);
      if (part.quantity.trim() === '' || !isQuantity(quantity) || quantity <= 0) next[`part-${index}`] = 'Enter a quantity above zero';
      else if (quantity > part.stock) next[`part-${index}`] = `Only ${formatNumber(part.stock)} in stock`;
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    act.mutate({ action: 'complete', data: { workDone: done.workDone.trim(), cause: done.cause.trim() || null, technician: technician.trim() || null, labourHours: hoursWorked, labourCost: cost, parts: parts.map((part) => ({ productId: part.productId, quantity: Number(part.quantity) })) } });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, TYPE[job.type].className)}>{TYPE[job.type].label}</span>
        <span className={cn(badgeClass, STATUS[job.status].className)}>{STATUS[job.status].label}</span>
        <span className="font-semibold text-gray-900">{job.machineCode} · {job.machineName}</span>
        <span>Priority {job.priority}</span>
        <span>Reported {formatMoment(job.reportedAt)}{job.reportedBy ? ` by ${job.reportedBy}` : ''}</span>
        {job.technician && <span>Technician {job.technician}</span>}
      </div>
      <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-900 ring-1 ring-gray-200">
        <p className="whitespace-pre-line">{job.problem}</p>
        {job.checklist && <p className="mt-2 whitespace-pre-line text-gray-700">{job.checklist}</p>}
        {job.stopsMachine && <p className={cn('mt-2 text-xs font-medium', pending ? 'text-red-600' : 'text-gray-500')}>{pending ? `The machine has been down for ${duration(job.elapsedMinutes)}` : `The machine was down for ${duration(job.elapsedMinutes)}`}{job.downtimeEntryId ? '; its work center is stopped in the downtime log' : ''}</p>}
      </div>

      {job.status === 'completed' && (
        <div className="space-y-2 text-sm text-gray-700">
          <p><span className="font-medium text-gray-900">Done:</span> {job.workDone}</p>
          {job.cause && <p><span className="font-medium text-gray-900">Cause:</span> {job.cause}</p>}
          <p>Completed {formatMoment(job.completedAt)}, {duration(job.elapsedMinutes)} after it was reported · labour {formatNumber(job.labourHours)} h, {formatMoney(job.labourCost)} · parts {formatMoney(job.partsCost)} · <span className="font-semibold text-gray-900">{formatMoney(job.totalCost)} in all</span></p>
          {(job.parts ?? []).length > 0 && (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {(job.parts ?? []).map((part) => (
                <li key={part.id} className="flex justify-between gap-3 px-3 py-1.5"><span>{part.productName} <span className="text-xs text-gray-500">{part.sku}</span></span><span className="tabular-nums">{formatNumber(part.quantity)} {part.unit} · {formatMoney(part.value)}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}
      {job.status === 'cancelled' && <p className="text-sm text-gray-700">Cancelled: {job.cancelReason}</p>}

      {mode === 'complete' && (
        <div className="space-y-4 rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="What was done" htmlFor="mc-done" required error={errors.workDone} className="sm:col-span-2">
              <textarea id="mc-done" rows={2} maxLength={2000} value={done.workDone} onChange={(event) => { setDone({ ...done, workDone: event.target.value }); setErrors({}); }} className={cn(inputClass, errors.workDone && inputErrorClass)} />
            </Field>
            {job.type !== 'preventive' && (
              <Field label="Why it failed" htmlFor="mc-cause" className="sm:col-span-2">
                <input id="mc-cause" type="text" maxLength={500} value={done.cause} onChange={(event) => setDone({ ...done, cause: event.target.value })} className={inputClass} />
              </Field>
            )}
            <Field label="Technician" htmlFor="mc-technician">
              <input id="mc-technician" type="text" maxLength={100} value={technician} onChange={(event) => setTechnician(event.target.value)} placeholder={job.technician ?? ''} className={inputClass} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Labour hours" htmlFor="mc-hours" error={errors.labourHours}>
                <input id="mc-hours" type="number" min="0" step="0.25" value={done.labourHours} onChange={(event) => setDone({ ...done, labourHours: event.target.value })} className={cn(inputClass, 'text-right tabular-nums')} />
              </Field>
              <Field label="Labour cost ₹" htmlFor="mc-cost" error={errors.labourCost}>
                <input id="mc-cost" type="number" min="0" step="0.01" value={done.labourCost} onChange={(event) => setDone({ ...done, labourCost: event.target.value })} className={cn(inputClass, 'text-right tabular-nums')} />
              </Field>
            </div>
          </div>
          <section className="rounded-lg border border-gray-200">
            <h4 className="rounded-t-lg border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Spare parts used · {parts.length}</h4>
            {parts.length > 0 && (
              <ul className="divide-y divide-gray-100">
                {parts.map((part, index) => (
                  <li key={part.productId} className="space-y-1 px-3 py-2">
                    <div className="flex items-center gap-3">
                      <p className="min-w-0 flex-1 text-sm text-gray-900">{part.name} <span className="text-xs text-gray-500">{part.sku} · {formatNumber(part.stock)} in stock</span></p>
                      <input type="number" min="0" step={QUANTITY_STEP} value={part.quantity} onChange={(event) => { setParts((prev) => prev.map((row, position) => (position === index ? { ...row, quantity: event.target.value } : row))); setErrors({}); }} placeholder="Qty" aria-label={`Quantity of ${part.name}`} className={cn(inputClass, 'w-28 py-1 text-right tabular-nums', errors[`part-${index}`] && inputErrorClass)} />
                      <span className="w-10 text-xs text-gray-500">{part.unit}</span>
                      <button type="button" onClick={() => setParts((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove ${part.name}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                    </div>
                    {errors[`part-${index}`] && <p className="text-sm text-red-600">{errors[`part-${index}`]}</p>}
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-gray-200 p-3">
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input id="mc-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search a part by name or SKU; it leaves stock when the job is completed" aria-label="Search parts" className={cn(inputClass, 'pl-9')} />
              </div>
              {search.length >= 2 && (
                <ul className="mt-1 max-h-40 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                  {found.filter((product) => !parts.some((part) => part.productId === product.id)).length === 0 ? (
                    <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No part matches'}</li>
                  ) : (
                    found.filter((product) => !parts.some((part) => part.productId === product.id)).map((product) => (
                      <li key={product.id}>
                        <button type="button" onClick={() => { setParts((prev) => [...prev, { productId: product.id, name: product.name, sku: product.sku, unit: product.unit, stock: Number(product.currentStock) || 0, quantity: '' }]); setSearchInput(''); setSearch(''); }} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50">
                          <span><span className="font-medium text-gray-900">{product.name}</span> <span className="text-gray-500">{product.sku}</span></span>
                          <span className="text-xs text-gray-500 tabular-nums">{formatNumber(Number(product.currentStock) || 0)} {product.unit}</span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          </section>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setMode('view')} disabled={act.isPending} className={secondaryButtonClass}>Back</button>
            <button type="button" onClick={complete} disabled={act.isPending} className={primaryButtonClass}>{act.isPending ? 'Saving…' : 'Confirm Completion'}</button>
          </div>
        </div>
      )}
      {mode === 'cancel' && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Field label="Why is the job cancelled?" htmlFor="mc-reason" required hint={job.downtimeEntryId ? 'The stop logged for the work center is removed with it' : undefined}>
            <input id="mc-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setMode('view')} className={secondaryButtonClass}>Back</button>
            <button type="button" disabled={act.isPending || reason.trim().length < 3} onClick={() => act.mutate({ action: 'cancel', data: { reason: reason.trim() } })} className={dangerButtonClass}>Confirm Cancelling</button>
          </div>
        </div>
      )}
      {mode === 'view' && (
        <div className="flex flex-wrap items-end justify-end gap-2 border-t border-gray-200 pt-3">
          <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
          {pending && canEdit && (
            <>
              <button type="button" onClick={() => setMode('cancel')} className={secondaryButtonClass}>Cancel Job</button>
              {job.status === 'open' && (
                <>
                  <input type="text" maxLength={100} value={technician} onChange={(event) => setTechnician(event.target.value)} placeholder="Technician" aria-label="Technician" className={cn(inputClass, 'w-44')} />
                  <button type="button" onClick={() => act.mutate({ action: 'start', data: { technician: technician.trim() || null } })} disabled={act.isPending} className={secondaryButtonClass}>Start Work</button>
                </>
              )}
              <button type="button" onClick={() => setMode('complete')} className={primaryButtonClass}>Complete Job</button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- a preventive task

const PlanForm: React.FC<{ plan: Plan | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ plan, isPending, onCancel, onSubmit }) => {
  const { data: machines = [] } = useMachineChoices();
  const [form, setForm] = useState({ machineId: plan?.machineId ?? '', name: plan?.name ?? '', frequencyDays: String(plan?.frequencyDays ?? 30), checklist: plan?.checklist ?? '', estimatedMinutes: plan?.estimatedMinutes == null ? '' : String(plan.estimatedMinutes), nextDueOn: plan?.nextDueOn ?? '', isActive: plan?.isActive ?? true });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors({});
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const frequency = Number(form.frequencyDays);
    if (!form.machineId) next.machineId = 'Choose the machine';
    if (form.name.trim().length < 2) next.name = 'Name the task';
    if (!Number.isInteger(frequency) || frequency < 1 || frequency > 3650) next.frequencyDays = 'A whole number of days, 1 to 3650';
    if (form.estimatedMinutes.trim() !== '' && (!Number.isInteger(Number(form.estimatedMinutes)) || Number(form.estimatedMinutes) < 0)) next.estimatedMinutes = 'Whole minutes';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ machineId: form.machineId, name: form.name.trim(), frequencyDays: frequency, checklist: form.checklist.trim() || null, estimatedMinutes: form.estimatedMinutes.trim() === '' ? null : Number(form.estimatedMinutes), nextDueOn: form.nextDueOn || null, isActive: form.isActive });
  };
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Machine" htmlFor="mp-machine" required error={errors.machineId}>
          <select id="mp-machine" value={form.machineId} disabled={!!plan} onChange={set('machineId')} className={cn(inputClass, errors.machineId && inputErrorClass)}>
            <option value="">Choose…</option>
            {plan && !machines.some((machine) => machine.id === plan.machineId) && <option value={plan.machineId}>{plan.machineCode} · {plan.machineName}</option>}
            {machines.map((machine) => (<option key={machine.id} value={machine.id}>{machine.code} · {machine.name}</option>))}
          </select>
        </Field>
        <Field label="Task" htmlFor="mp-name" required error={errors.name}>
          <input id="mp-name" type="text" maxLength={120} value={form.name} onChange={set('name')} placeholder="Lubricate slideways" className={cn(inputClass, errors.name && inputErrorClass)} />
        </Field>
        <Field label="Every (days)" htmlFor="mp-frequency" required error={errors.frequencyDays}>
          <input id="mp-frequency" type="number" min="1" step="1" value={form.frequencyDays} onChange={set('frequencyDays')} className={cn(inputClass, errors.frequencyDays && inputErrorClass)} />
        </Field>
        <Field label="Next due on" htmlFor="mp-due" hint={plan ? 'Completing its job moves this on by itself' : 'Today when left empty'}>
          <input id="mp-due" type="date" value={form.nextDueOn} onChange={set('nextDueOn')} className={inputClass} />
        </Field>
        <Field label="What to do, a step to a line" htmlFor="mp-checklist" className="sm:col-span-2">
          <textarea id="mp-checklist" rows={3} maxLength={2000} value={form.checklist} onChange={set('checklist')} className={inputClass} />
        </Field>
        <Field label="Usually takes (minutes)" htmlFor="mp-minutes" error={errors.estimatedMinutes}>
          <input id="mp-minutes" type="number" min="0" step="1" value={form.estimatedMinutes} onChange={set('estimatedMinutes')} className={inputClass} />
        </Field>
        {plan && (
          <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isActive} onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
            In use
          </label>
        )}
      </div>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : plan ? 'Save Task' : 'Add Task'}</button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- a machine

const MachineForm: React.FC<{ machine: Machine | null; isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ machine, isPending, onCancel, onSubmit }) => {
  const { data: workCenters = [] } = useQuery({ queryKey: ['maintenance', 'work-centers'], queryFn: maintenanceApi.workCenters });
  const [form, setForm] = useState({ code: machine?.code ?? '', name: machine?.name ?? '', workCenterId: machine?.workCenterId ?? '', location: machine?.location ?? '', make: machine?.make ?? '', model: machine?.model ?? '', serialNumber: machine?.serialNumber ?? '', commissionedOn: machine?.commissionedOn ?? '', notes: machine?.notes ?? '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [field]: field === 'code' ? event.target.value.toUpperCase() : event.target.value }));
    setErrors({});
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!/^[A-Z0-9][A-Z0-9/-]{1,29}$/.test(form.code.trim())) next.code = '2 to 30 capitals, digits, hyphens or slashes';
    if (form.name.trim().length < 2) next.name = 'Name the machine';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({ ...form, code: form.code.trim(), name: form.name.trim(), workCenterId: form.workCenterId || null, commissionedOn: form.commissionedOn || null });
  };
  const input = (field: keyof typeof form, label: string, extra: { required?: boolean; placeholder?: string; maxLength?: number; type?: string; className?: string } = {}) => (
    <Field label={label} htmlFor={`mm-${field}`} required={extra.required} error={errors[field]} className={extra.className}>
      <input id={`mm-${field}`} type={extra.type ?? 'text'} maxLength={extra.maxLength} placeholder={extra.placeholder} value={form[field]} onChange={set(field)} className={cn(inputClass, errors[field] && inputErrorClass)} />
    </Field>
  );
  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {input('code', 'Code', { required: true, placeholder: 'CNC-03', maxLength: 30 })}
        {input('name', 'Name', { required: true, placeholder: 'CNC turning centre', maxLength: 100, className: 'sm:col-span-2' })}
        <Field label="Work center" htmlFor="mm-workCenterId" hint="A breakdown can stop it in the downtime log">
          <select id="mm-workCenterId" value={form.workCenterId} onChange={set('workCenterId')} className={inputClass}>
            <option value="">None</option>
            {workCenters.map((center) => (<option key={center.id} value={center.id}>{center.name}</option>))}
          </select>
        </Field>
        {input('location', 'Stands at', { maxLength: 100 })}
        {input('commissionedOn', 'Commissioned on', { type: 'date' })}
        {input('make', 'Make', { maxLength: 60 })}
        {input('model', 'Model', { maxLength: 60 })}
        {input('serialNumber', 'Serial number', { maxLength: 60 })}
        {input('notes', 'Notes', { maxLength: 500, className: 'sm:col-span-3' })}
      </div>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>{isPending ? 'Saving…' : machine ? 'Save Machine' : 'Add Machine'}</button>
      </FormActions>
    </form>
  );
};

const MachinePanel: React.FC<{ machineId: string; canEdit: boolean; canDelete: boolean; onEdit: (machine: Machine) => void; onOpenJob: (id: string) => void; onClose: () => void; onChanged: () => void }> = ({ machineId, canEdit, canDelete, onEdit, onOpenJob, onClose, onChanged }) => {
  const queryClient = useQueryClient();
  const { data: machine, isLoading, isError, error } = useQuery({ queryKey: ['maintenance', 'machine', machineId], queryFn: () => maintenanceApi.machine(machineId) });
  const fail = (actionError: unknown) => toast.error(getApiErrorMessage(actionError, 'That could not be done'), { duration: 7000 });
  const act = useMutation({
    mutationFn: (action: 'retire' | 'reinstate') => maintenanceApi.machineAction(machineId, action),
    onSuccess: (saved) => {
      toast.success(saved.status === 'retired' ? `${saved.code} retired` : `${saved.code} is back in the register`);
      queryClient.setQueryData(['maintenance', 'machine', machineId], saved);
      onChanged();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: () => maintenanceApi.removeMachine(machineId),
    onSuccess: () => {
      toast.success('Machine deleted');
      onChanged();
      onClose();
    },
    onError: fail,
  });
  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !machine) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The machine could not be loaded.')}</p>;
  const reliability = machine.reliability;
  const figures = [
    { label: 'Breakdowns', value: formatNumber(reliability.breakdowns) },
    { label: 'Time to repair', value: reliability.meanRepairHours === null ? '—' : `${formatNumber(reliability.meanRepairHours, 1)} h` },
    { label: 'Between breakdowns', value: reliability.meanDaysBetweenBreakdowns === null ? '—' : `${formatNumber(reliability.meanDaysBetweenBreakdowns, 1)} days` },
    { label: 'Time down', value: `${formatNumber(reliability.hoursDown, 1)} h` },
    { label: 'Spent', value: formatMoney(reliability.cost) },
  ];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
        <span className={cn(badgeClass, MACHINE[machine.status].className)}>{MACHINE[machine.status].label}</span>
        <span className="font-semibold text-gray-900">{machine.name}</span>
        {[machine.make, machine.model, machine.serialNumber && `S/N ${machine.serialNumber}`].filter(Boolean).map((part) => (<span key={String(part)}>{part}</span>))}
        {machine.workCenterName && <span>Work center {machine.workCenterName}</span>}
        {machine.location && <span>Stands at {machine.location}</span>}
        {machine.commissionedOn && <span>Commissioned {formatDate(machine.commissionedOn)}</span>}
      </div>
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">The last {reliability.days} days</h4>
        <div className="mt-2 grid grid-cols-2 gap-3 rounded-lg bg-gray-50 p-3 text-sm ring-1 ring-gray-200 sm:grid-cols-5">
          {figures.map((figure) => (<div key={figure.label}><p className="text-xs uppercase tracking-wide text-gray-500">{figure.label}</p><p className="font-medium text-gray-900">{figure.value}</p></div>))}
        </div>
      </div>
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Preventive tasks · {machine.plans.length}</h4>
        {machine.plans.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No preventive tasks. Add them on the Preventive tasks tab.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
            {machine.plans.map((plan) => (
              <li key={plan.id} className="flex flex-wrap items-baseline gap-x-4 px-4 py-2">
                <span className={cn('min-w-0 flex-1', plan.isActive ? 'text-gray-900' : 'text-gray-400')}>{plan.name}</span>
                <span className="text-xs text-gray-500">every {formatNumber(plan.frequencyDays)} days</span>
                <span className={cn('text-xs', !plan.isActive ? 'text-gray-400' : plan.due === 'overdue' ? 'font-medium text-red-600' : plan.due === 'soon' ? 'text-amber-700' : 'text-gray-500')}>{plan.isActive ? `${formatDate(plan.nextDueOn)} · ${dueText(plan)}` : 'Switched off'}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Recent jobs · {machine.jobs.length}</h4>
        {machine.jobs.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No maintenance job has been raised on this machine.</p>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
            {machine.jobs.map((job) => (
              <li key={job.id} className="flex flex-wrap items-baseline gap-x-4 px-4 py-2">
                <button type="button" onClick={() => onOpenJob(job.id)} className="font-medium text-indigo-600 hover:text-indigo-500">{job.jobNumber}</button>
                <span className={cn(badgeClass, TYPE[job.type].className)}>{TYPE[job.type].label}</span>
                <span className="min-w-0 flex-1 truncate text-gray-700">{job.problem}</span>
                <span className="text-xs text-gray-500">{formatDate(job.reportedAt)} · {STATUS[job.status].label}{job.totalCost > 0 ? ` · ${formatMoney(job.totalCost)}` : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
      <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-3">
        <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
        {canDelete && machine.jobs.length === 0 && <button type="button" onClick={() => remove.mutate()} disabled={remove.isPending} className={secondaryButtonClass}>Delete</button>}
        {canEdit && (
          <>
            {machine.status === 'retired' ? (
              <button type="button" onClick={() => act.mutate('reinstate')} disabled={act.isPending} className={secondaryButtonClass}>Bring Back</button>
            ) : (
              <button type="button" onClick={() => act.mutate('retire')} disabled={act.isPending} className={secondaryButtonClass}>Retire</button>
            )}
            <button type="button" onClick={() => onEdit(machine)} className={primaryButtonClass}>Edit</button>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- the page

type Dialog = { kind: 'newJob' } | { kind: 'job'; id: string } | { kind: 'plan'; plan: Plan | null } | { kind: 'machine'; id: string } | { kind: 'machineForm'; machine: Machine | null } | null;

const Maintenance: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('manufacturing.create');
  const canEdit = can('manufacturing.update');
  const canDelete = can('manufacturing.delete');
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>('jobs');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [jobStatus, setJobStatus] = useState('pending');
  const [jobType, setJobType] = useState('');
  const [planDue, setPlanDue] = useState('');
  const [machineStatus, setMachineStatus] = useState('');
  const [dialog, setDialog] = useState<Dialog>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: summary } = useQuery({ queryKey: ['maintenance', 'summary'], queryFn: maintenanceApi.summary });
  const jobs = useQuery({ queryKey: ['maintenance', 'jobs', { page, limit, search, status: jobStatus, type: jobType }], queryFn: () => maintenanceApi.jobs({ page, limit, search, status: jobStatus, type: jobType }), placeholderData: keepPreviousData, enabled: tab === 'jobs' });
  const plans = useQuery({ queryKey: ['maintenance', 'plans', { page, limit, search, due: planDue }], queryFn: () => maintenanceApi.plans({ page, limit, search, due: planDue }), placeholderData: keepPreviousData, enabled: tab === 'plans' });
  const machines = useQuery({ queryKey: ['maintenance', 'machines', { page, limit, search, status: machineStatus }], queryFn: () => maintenanceApi.machines({ page, limit, search, status: machineStatus }), placeholderData: keepPreviousData, enabled: tab === 'machines' });
  const current = tab === 'jobs' ? jobs : tab === 'plans' ? plans : machines;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['maintenance'] });
  const fail = (fallback: string) => (error: unknown) => toast.error(getApiErrorMessage(error, fallback), { duration: 7000 });
  const createJob = useMutation({
    mutationFn: (input: object) => maintenanceApi.createJob(input),
    onSuccess: (job) => {
      toast.success(`${job.jobNumber} raised on ${job.machineCode}${job.downtimeEntryId ? '; its work center is stopped in the downtime log' : ''}`);
      refresh();
      queryClient.invalidateQueries({ queryKey: ['oee'] });
      setTab('jobs');
      setDialog({ kind: 'job', id: job.id });
    },
    onError: fail('The job could not be raised'),
  });
  const savePlan = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: object }) => maintenanceApi.savePlan(id, input),
    onSuccess: (plan, { id }) => {
      toast.success(id ? `"${plan.name}" saved` : `"${plan.name}" added to ${plan.machineCode}`);
      refresh();
      setDialog(null);
    },
    onError: fail('The task could not be saved'),
  });
  const removePlan = useMutation({
    mutationFn: (plan: Plan) => maintenanceApi.removePlan(plan.id),
    onSuccess: (_result, plan) => {
      toast.success(`"${plan.name}" deleted`);
      refresh();
    },
    onError: fail('The task could not be deleted'),
  });
  const raise = useMutation({
    mutationFn: (plan: Plan) => maintenanceApi.raise(plan.id),
    onSuccess: (job) => {
      toast.success(`${job.jobNumber} raised for "${job.problem}"`);
      refresh();
      setDialog({ kind: 'job', id: job.id });
    },
    onError: fail('The job could not be raised'),
  });
  const saveMachine = useMutation({
    mutationFn: ({ id, input }: { id: string | null; input: object }) => maintenanceApi.saveMachine(id, input),
    onSuccess: (machine, { id }) => {
      toast.success(id ? `${machine.code} saved` : `${machine.code} added`);
      queryClient.setQueryData(['maintenance', 'machine', machine.id], machine);
      refresh();
      setDialog({ kind: 'machine', id: machine.id });
    },
    onError: fail('The machine could not be saved'),
  });

  const total = current.data?.total ?? 0;
  const totalPages = current.data?.totalPages ?? 1;
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const switchTab = (next: Tab) => {
    setTab(next);
    setPage(1);
    setSearchInput('');
    setSearch('');
  };
  const stats = [
    { label: 'Machines down', value: formatNumber(summary?.machinesDown ?? 0), hint: `${formatNumber(summary?.machinesRunning ?? 0)} running`, alert: (summary?.machinesDown ?? 0) > 0 },
    { label: 'Jobs not finished', value: formatNumber(summary?.openJobs ?? 0), hint: `${formatNumber(summary?.breakdownsThisMonth ?? 0)} breakdown(s) this month` },
    { label: 'Preventive tasks overdue', value: formatNumber(summary?.plansOverdue ?? 0), hint: `${formatNumber(summary?.plansDueThisWeek ?? 0)} more due this week`, alert: (summary?.plansOverdue ?? 0) > 0 },
    { label: 'Spent this month', value: formatMoney(summary?.costThisMonth ?? 0), hint: 'Parts and labour on completed jobs' },
  ];
  const tabs: { key: Tab; label: string }[] = [
    { key: 'jobs', label: 'Jobs' },
    { key: 'plans', label: 'Preventive tasks' },
    { key: 'machines', label: 'Machines' },
  ];
  const emptyRow = (columns: number, title: string, hint: string) => (
    <tr>
      <td colSpan={columns} className="px-4 py-12 text-center">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="mt-1 text-sm text-gray-500">{hint}</p>
      </td>
    </tr>
  );
  const stateRow = (columns: number) =>
    current.isError ? (
      <tr><td colSpan={columns} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(current.error, 'This could not be loaded.')}</td></tr>
    ) : current.isLoading ? (
      <tr><td colSpan={columns} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
    ) : null;
  const openButton = (label: string, onClick: () => void, text = 'Open') => (
    <button type="button" onClick={onClick} className="rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50" aria-label={label}>{text}</button>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Maintenance</h1>
          <p className="mt-1 text-sm text-gray-500">The machines of the plant, the preventive work that falls due on them, and the breakdowns and repairs, with the parts and time they took.</p>
        </div>
        {canCreate && (
          <div className="flex flex-wrap gap-3">
            {tab === 'plans' && <button type="button" onClick={() => setDialog({ kind: 'plan', plan: null })} className={secondaryButtonClass}><PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />Add Task</button>}
            {tab === 'machines' && <button type="button" onClick={() => setDialog({ kind: 'machineForm', machine: null })} className={secondaryButtonClass}><PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />Add Machine</button>}
            <button type="button" onClick={() => setDialog({ kind: 'newJob' })} className={primaryButtonClass}><PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />Report a Problem</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-red-600' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex gap-1 border-b border-gray-200 px-4 pt-3" role="tablist">
          {tabs.map((item) => (
            <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => switchTab(item.key)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>{item.label}</button>
          ))}
        </div>
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder={tab === 'jobs' ? 'Search by job number, machine, problem or technician' : tab === 'plans' ? 'Search by task or machine' : 'Search by code, name, make, serial number, location or work center'} aria-label="Search" className={cn(inputClass, 'pl-9')} />
          </div>
          {tab === 'jobs' && (
            <>
              <select value={jobStatus} onChange={(event) => { setJobStatus(event.target.value); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-44')}>
                <option value="pending">Not finished</option>
                <option value="">All jobs</option>
                {(Object.keys(STATUS) as JobStatus[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
              </select>
              <select value={jobType} onChange={(event) => { setJobType(event.target.value); setPage(1); }} aria-label="Kind" className={cn(inputClass, 'lg:w-40')}>
                <option value="">All kinds</option>
                {(Object.keys(TYPE) as JobType[]).map((type) => (<option key={type} value={type}>{TYPE[type].label}</option>))}
              </select>
            </>
          )}
          {tab === 'plans' && (
            <select value={planDue} onChange={(event) => { setPlanDue(event.target.value); setPage(1); }} aria-label="Due" className={cn(inputClass, 'lg:w-48')}>
              <option value="">All tasks</option>
              <option value="overdue">Overdue</option>
              <option value="week">Due within a week</option>
            </select>
          )}
          {tab === 'machines' && (
            <select value={machineStatus} onChange={(event) => { setMachineStatus(event.target.value); setPage(1); }} aria-label="Machine status" className={cn(inputClass, 'lg:w-40')}>
              <option value="">All machines</option>
              {(Object.keys(MACHINE) as MachineRow['status'][]).map((status) => (<option key={status} value={status}>{MACHINE[status].label}</option>))}
            </select>
          )}
        </div>

        <div className={cn('overflow-x-auto', current.isFetching && current.data && 'opacity-60')}>
          {tab === 'jobs' && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Job</th>
                  <th scope="col" className={thClass}>Machine</th>
                  <th scope="col" className={thClass}>Problem</th>
                  <th scope="col" className={thClass}>Reported</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stateRow(6) ??
                  ((jobs.data?.data ?? []).length === 0
                    ? emptyRow(6, search || jobType || jobStatus !== 'pending' ? 'No job matches' : 'No unfinished jobs', 'Report a problem on a machine, or raise the job for a preventive task that is due.')
                    : (jobs.data?.data ?? []).map((job) => {
                        const pending = job.status === 'open' || job.status === 'in_progress';
                        return (
                          <tr key={job.id} className={cn('hover:bg-gray-50', pending && job.stopsMachine && 'bg-red-50/40')}>
                            <td className="whitespace-nowrap px-4 py-3">
                              <button type="button" onClick={() => setDialog({ kind: 'job', id: job.id })} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{job.jobNumber}</button>
                              <p className="mt-0.5"><span className={cn(badgeClass, TYPE[job.type].className)}>{TYPE[job.type].label}</span></p>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">{job.machineCode}<span className="block text-xs text-gray-500">{job.machineName}</span></td>
                            <td className="max-w-sm px-4 py-3 text-sm text-gray-700"><span className="line-clamp-2">{job.problem}</span>{pending && job.stopsMachine && <span className="block text-xs font-medium text-red-600">Machine down for {duration(job.elapsedMinutes)}</span>}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatMoment(job.reportedAt)}{job.technician && <span className="block text-xs text-gray-500">{job.technician}</span>}</td>
                            <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[job.status].className)}>{STATUS[job.status].label}</span>{job.status === 'completed' && job.totalCost > 0 && <span className="mt-0.5 block text-xs text-gray-500">{formatMoney(job.totalCost)}</span>}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-right">{openButton(`Open ${job.jobNumber}`, () => setDialog({ kind: 'job', id: job.id }))}</td>
                          </tr>
                        );
                      }))}
              </tbody>
            </table>
          )}
          {tab === 'plans' && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Task</th>
                  <th scope="col" className={thClass}>Machine</th>
                  <th scope="col" className={thClass}>Every</th>
                  <th scope="col" className={thClass}>Last done</th>
                  <th scope="col" className={thClass}>Next due</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stateRow(6) ??
                  ((plans.data?.data ?? []).length === 0
                    ? emptyRow(6, search || planDue ? 'No task matches' : 'No preventive tasks yet', 'Add the work that is done on a machine at intervals: lubrication, filter changes, checks.')
                    : (plans.data?.data ?? []).map((plan) => (
                        <tr key={plan.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{plan.name}{plan.estimatedMinutes ? <span className="block text-xs font-normal text-gray-500">about {duration(plan.estimatedMinutes)}</span> : null}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{plan.machineCode}<span className="block text-xs text-gray-500">{plan.machineName}</span></td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatNumber(plan.frequencyDays)} days</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatDate(plan.lastDoneOn)}</td>
                          <td className={cn('whitespace-nowrap px-4 py-3 text-sm', plan.due === 'overdue' ? 'font-medium text-red-600' : 'text-gray-700')}>
                            {formatDate(plan.nextDueOn)}
                            <span className={cn('block text-xs', plan.due === 'overdue' ? 'text-red-600' : plan.due === 'soon' ? 'text-amber-700' : 'font-normal text-gray-500')}>{dueText(plan)}</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            {plan.openJobNumber ? (
                              <span className="mr-2 text-xs text-gray-500">{plan.openJobNumber} is open</span>
                            ) : (
                              canCreate && <span className="mr-1">{openButton(`Raise the job for ${plan.name} on ${plan.machineCode}`, () => raise.mutate(plan), 'Raise Job')}</span>
                            )}
                            {canEdit && <span className="mr-1">{openButton(`Edit ${plan.name} on ${plan.machineCode}`, () => setDialog({ kind: 'plan', plan }), 'Edit')}</span>}
                            {canDelete && !plan.lastDoneOn && !plan.openJobNumber && (
                              <button type="button" onClick={() => removePlan.mutate(plan)} disabled={removePlan.isPending} aria-label={`Delete ${plan.name} on ${plan.machineCode}`} className="rounded-md p-1.5 align-middle text-gray-400 hover:bg-red-50 hover:text-red-600"><TrashIcon className="h-4 w-4" /></button>
                            )}
                          </td>
                        </tr>
                      )))}
              </tbody>
            </table>
          )}
          {tab === 'machines' && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Machine</th>
                  <th scope="col" className={thClass}>Work center</th>
                  <th scope="col" className={thClass}>Stands at</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Open jobs</th>
                  <th scope="col" className={thClass}>Next task due</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {stateRow(7) ??
                  ((machines.data?.data ?? []).length === 0
                    ? emptyRow(7, search || machineStatus ? 'No machine matches' : 'No machines yet', 'Add the machines and equipment that are maintained.')
                    : (machines.data?.data ?? []).map((machine) => (
                        <tr key={machine.id} className={cn('hover:bg-gray-50', machine.status === 'retired' && 'bg-gray-50/60')}>
                          <td className="px-4 py-3">
                            <button type="button" onClick={() => setDialog({ kind: 'machine', id: machine.id })} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">{machine.code}</button>
                            <p className="mt-0.5 text-xs text-gray-500">{[machine.name, machine.make].filter(Boolean).join(' · ')}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{machine.workCenterName || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{machine.location || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatNumber(machine.openJobs)}</td>
                          <td className={cn('whitespace-nowrap px-4 py-3 text-sm', machine.overduePlans > 0 ? 'font-medium text-red-600' : 'text-gray-700')}>{formatDate(machine.nextDueOn)}{machine.overduePlans > 0 && <span className="block text-xs">{machine.overduePlans} overdue</span>}</td>
                          <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, MACHINE[machine.status].className)}>{MACHINE[machine.status].label}</span></td>
                          <td className="whitespace-nowrap px-4 py-3 text-right">{openButton(`Open ${machine.code}`, () => setDialog({ kind: 'machine', id: machine.id }))}</td>
                        </tr>
                      )))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal
        isOpen={!!dialog}
        onClose={() => setDialog(null)}
        title={!dialog ? '' : dialog.kind === 'newJob' ? 'Report a Problem' : dialog.kind === 'job' ? 'Maintenance Job' : dialog.kind === 'plan' ? (dialog.plan ? 'Edit Preventive Task' : 'Add Preventive Task') : dialog.kind === 'machine' ? 'Machine' : dialog.machine ? `Edit ${dialog.machine.code}` : 'Add Machine'}
        size="xl"
      >
        {dialog?.kind === 'newJob' && <JobForm isPending={createJob.isPending} onCancel={() => setDialog(null)} onSubmit={(input) => createJob.mutate(input)} />}
        {dialog?.kind === 'job' && <JobPanel key={dialog.id} jobId={dialog.id} canEdit={canEdit} onClose={() => setDialog(null)} onChanged={refresh} />}
        {dialog?.kind === 'plan' && <PlanForm key={dialog.plan?.id ?? 'new'} plan={dialog.plan} isPending={savePlan.isPending} onCancel={() => setDialog(null)} onSubmit={(input) => savePlan.mutate({ id: dialog.plan?.id ?? null, input })} />}
        {dialog?.kind === 'machine' && (
          <MachinePanel key={dialog.id} machineId={dialog.id} canEdit={canEdit} canDelete={canDelete} onEdit={(machine) => setDialog({ kind: 'machineForm', machine })} onOpenJob={(id) => setDialog({ kind: 'job', id })} onClose={() => setDialog(null)} onChanged={refresh} />
        )}
        {dialog?.kind === 'machineForm' && <MachineForm key={dialog.machine?.id ?? 'new'} machine={dialog.machine} isPending={saveMachine.isPending} onCancel={() => setDialog(null)} onSubmit={(input) => saveMachine.mutate({ id: dialog.machine?.id ?? null, input })} />}
      </Modal>
    </div>
  );
};

export default Maintenance;
